import dayjs from "dayjs";

import { provName, toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import {
  formatAggregateCurrency,
  formatAggregateNetWeightKg,
  formatAggregateTotalWeight,
} from "./constants";
import {
  getAvgFromBeefAiAggregateRow,
  getAvgNetWeightFromBeefAiAggregateRow,
  getAvgTotalWeightFromBeefAiAggregateRow,
  getMaxFromBeefAiAggregateRow,
  getMaxNetWeightFromBeefAiAggregateRow,
  getMaxTotalWeightFromBeefAiAggregateRow,
  getMinFromBeefAiAggregateRow,
  getMinNetWeightFromBeefAiAggregateRow,
  getMinTotalWeightFromBeefAiAggregateRow,
} from "./productSnapshotChartUtils";

/** Directus aggregate `groupBy[]=product_id` may return a UUID string or a nested `{ id }`. */
export function normalizeAggregateProductId(aggregateRow) {
  const raw = aggregateRow?.product_id;
  if (raw == null) return "";
  if (typeof raw === "object" && raw.id != null) return String(raw.id);
  return String(raw);
}

/**
 * Maps Directus aggregate rows (`year(record_at)`, `month(record_at)`, …) to pricing report table rows.
 * @param {any[]} aggregateRows
 * @param {Map<string, string>} productIdToName merged catalog + per-page product `name` lookups
 */
export function mapAggregateRowsToPriceReportRows(aggregateRows, productIdToName) {
  const nameById = productIdToName instanceof Map ? productIdToName : new Map();
  return (aggregateRows || [])
    .map((r) => {
      const y = r.record_at_year;
      const mo = r.record_at_month;
      const month = y != null && mo != null ? `${y}-${String(mo).padStart(2, "0")}` : "";
      const pid = normalizeAggregateProductId(r);
      const productName = nameById.get(pid) || pid || "—";
      const provinceCode = toProv(r.province);
      const province = provinceCode ? provName(provinceCode) : r.province ?? "";
      const provider = r.provider ?? "";
      const unitType = r.unit_type ?? "";
      const unit = r.unit ?? "";
      const avgUnitValue = getAvgFromBeefAiAggregateRow(r);
      const avgUnit = Number.isFinite(avgUnitValue) ? avgUnitValue : null;
      const lowUnitValue = getMinFromBeefAiAggregateRow(r);
      const lowUnit = lowUnitValue != null && Number.isFinite(lowUnitValue) ? lowUnitValue : null;
      const highUnitValue = getMaxFromBeefAiAggregateRow(r);
      const highUnit = highUnitValue != null && Number.isFinite(highUnitValue) ? highUnitValue : null;
      const avgTotalWeightRaw = getAvgTotalWeightFromBeefAiAggregateRow(r);
      const lowTotalWeightRaw = getMinTotalWeightFromBeefAiAggregateRow(r);
      const highTotalWeightRaw = getMaxTotalWeightFromBeefAiAggregateRow(r);
      const avgNetWeight = getAvgNetWeightFromBeefAiAggregateRow(r);
      const lowNetWeight = getMinNetWeightFromBeefAiAggregateRow(r);
      const highNetWeight = getMaxNetWeightFromBeefAiAggregateRow(r);
      const avgTotalWeight = avgTotalWeightRaw ?? avgNetWeight;
      const lowTotalWeight = lowTotalWeightRaw ?? lowNetWeight;
      const highTotalWeight = highTotalWeightRaw ?? highNetWeight;
      const n = Number(r.count ?? 0);
      return {
        month,
        productName,
        province,
        retailer: r.retailer ?? "",
        provider,
        unitType,
        unit,
        avgUnit,
        lowUnit,
        highUnit,
        avgTotalWeight,
        lowTotalWeight,
        highTotalWeight,
        avgNetWeight,
        lowNetWeight,
        highNetWeight,
        n,
        drilldown: {
          year: y != null ? Number(y) : null,
          month: mo != null ? Number(mo) : null,
          province: r.province ?? "",
          retailer: r.retailer ?? "",
          productId: pid,
          provider,
          unitType,
          unit,
        },
      };
    });
}

function readNumericField(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Maps filtered `price_history` rows to CSV detail export rows (one row per record).
 *
 * @param {any[]} records
 * @param {Map<string, string>} productIdToName
 */
export function mapPriceHistoryRecordsToRawExportRows(records, productIdToName) {
  const nameById = productIdToName instanceof Map ? productIdToName : new Map();
  return (records || []).map((row) => {
    const pid = normalizeAggregateProductId(row);
    const productName = nameById.get(pid) || row.product_name || pid || "—";
    const provinceCode = toProv(row.province);
    const province = provinceCode ? provName(provinceCode) : row.province ?? "";
    const rawUnitQuantity = readNumericField(row.raw_unit_quantity);
    const unitQuantity = readNumericField(row.unit_quantity);
    const totalWeightValue = rawUnitQuantity ?? unitQuantity;
    return {
      recordDate: row.record_at ? dayjs(row.record_at).format("YYYY-MM-DD") : "",
      productName,
      province,
      retailer: row.retailer ?? "",
      provider: row.provider ?? "",
      unitPrice: formatAggregateCurrency(readNumericField(row.unit_price)),
      totalWeight: formatAggregateTotalWeight(totalWeightValue),
      netWeight: formatAggregateNetWeightKg(unitQuantity),
    };
  });
}
